// PurchaseClaimList.jsx

import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";

const STATUS_STYLES = {
    Pending:        "bg-yellow-900/30 text-yellow-400 border-yellow-700/40",
    Approved:       "bg-green-900/30 text-green-400 border-green-700/40",
    PartialApproved:"bg-blue-900/30 text-blue-400 border-blue-700/40",
    Rejected:       "bg-red-900/30 text-red-400 border-red-700/40",
    Cancelled:      "bg-gray-900/30 text-gray-400 border-gray-700/40",
};

const ClaimCard = ({ claim, isSelected, onToggle, canSelect }) => {
    const isRedeemable = ["Approved", "PartialApproved"].includes(claim.status) && !claim.redemptionId;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-gray-900 rounded-xl border p-3 space-y-2 transition-colors ${
                isSelected
                    ? "border-cyan-500/60 bg-cyan-950/20"
                    : "border-gray-700/60"
            }`}
        >
            <div className="flex items-start gap-3">
                {/* Checkbox — only for redeemable */}
                {canSelect && isRedeemable && (
                    <button
                        onClick={() => onToggle(claim)}
                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            isSelected
                                ? "bg-cyan-500 border-cyan-500"
                                : "border-gray-600 bg-transparent"
                        }`}
                    >
                        {isSelected && <span className="text-black text-[10px] font-black">✓</span>}
                    </button>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="text-sm font-bold text-white truncate">
                                🧾 {claim.billNo}
                            </div>
                            <div className="text-[11px] text-gray-400">
                                🏪 {claim.shopName || claim.shopkeeperName}
                            </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                            <div className="text-green-400 font-black">
                                ₹{claim.billAmount}
                            </div>
                            <div className="text-[10px] text-gray-500">
                                {new Date(claim.billDate).toLocaleDateString("en-IN")}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-gray-500">Calculated:</span>
                            <span className="text-yellow-400 font-bold">{claim.calculatedStreakPoints} pts</span>
                            {claim.approvedStreakPoints > 0 && (
                                <>
                                    <span className="text-gray-600">→</span>
                                    <span className="text-green-400 font-bold">{claim.approvedStreakPoints} approved</span>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <span
                                className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${
                                    STATUS_STYLES[claim.status] || ""
                                }`}
                            >
                                {claim.status}
                            </span>

                            {claim.redemptionId && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-900/40 border border-purple-600 text-purple-300 font-bold">
                                    Vouchered
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const PurchaseClaimList = ({ claims = [], selectedClaims = [], setSelectedClaims, onRedeemSuccess }) => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;
    const user  = authState?.user;

    const [shopFilter, setShopFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [loading,  setLoading]  = useState(false);
    const [message,  setMessage]  = useState(null);

    const shopOptions = useMemo(() => {
        const map = {};
        claims.forEach((c) => {
            if (c.shopkeeperCode) map[c.shopkeeperCode] = { shopkeeperCode: c.shopkeeperCode, shopName: c.shopName };
        });
        return Object.values(map);
    }, [claims]);

    const filteredClaims = useMemo(() => {
        return claims.filter((c) => {
            const shopMatch   = shopFilter   === "ALL" || c.shopkeeperCode === shopFilter;
            const statusMatch = statusFilter === "ALL" || c.status === statusFilter;
            return shopMatch && statusMatch;
        });
    }, [claims, shopFilter, statusFilter]);

    const redeemableClaims = filteredClaims.filter((c) =>
            ["Approved", "PartialApproved"].includes(c.status) && !c.redemptionId
    );

    const isSelected = (claim) => selectedClaims.some((c) => c._id === claim._id);

    const toggleSelect = (claim) => {
        if (
            !["Approved", "PartialApproved"].includes(claim.status) ||
            claim.redemptionId
        ) {
            return;
        }
        if (isSelected(claim)) {
            setSelectedClaims((p) => p.filter((c) => c._id !== claim._id));

        } else {
            setSelectedClaims((p) => {
                if (!p.length) return [...p, claim];
                if (p[0].shopkeeperCode !== claim.shopkeeperCode) {
                    setMessage({
                        type: "error",
                        text: "A voucher can contain approved claims from one merchant only.",
                    });
                    return p;
                }

                return [...p, claim];
            });
        }
    };

    const selectedTotal = selectedClaims.reduce((sum, c) => sum + (c.approvedStreakPoints || 0), 0);
    const canCreateVoucher = selectedClaims.length > 0 && selectedTotal >= 100;

    const [refreshing, setRefreshing] = useState(false);
    const refreshClaims = async () => {
        try {
            setRefreshing(true);
            const res = await fetchData({
                url: "/pd/streaks/claim-my",
                method: "post",
                data: { customerId: user?._id, customerCode: user?.think5whyscode },
                headers: { Authorization: token },
            });
            if (res?.success) {
                onRedeemSuccess?.(res.data); // reuse existing callback to push fresh data up to parent
            }
        } catch (err) {
            console.error("Refresh claims error:", err);
        } finally {
            setRefreshing(false);
        }
    };

    const submitRedemption = async () => {
        if (!selectedClaims.length) return;
        try {
            setLoading(true);
            setMessage(null);

            // Group by shopkeeper — one voucher request per merchant
            const grouped = selectedClaims.reduce((acc, c) => {
                const key = c.shopkeeperCode;
                if (!acc[key]) {
                    acc[key] = {
                        userId:         user?._id,
                        userCode:       user?.think5whyscode,
                        userName:       user?.name,
                        shopkeeperCode: c.shopkeeperCode,
                        shopkeeperName: c.shopkeeperName,
                        shopName:       c.shopName,
                        redemptionType: "Discount",
                        remarks:        "Claim-based redemption",
                        requestedPoints: 0,
                        claimIds: [],
                    };
                }
                acc[key].requestedPoints += c.approvedStreakPoints || 0;
                acc[key].claimIds.push(c._id);
                return acc;
            }, {});

            const res = await fetchData({
                url: "/pd/streaks/redemption-create",
                method: "post",
                data: { requests: Object.values(grouped) },
                headers: { Authorization: token },
            });

            if (!res?.success) throw new Error(res?.message || "Failed");

            setMessage({ type: "success", text: `🎉 Voucher created for ${selectedTotal} pts` });
            setSelectedClaims([]);
            onRedeemSuccess?.();
        } catch (err) {
            setMessage({ type: "error", text: err.message });
        } finally {
            setLoading(false);
        }
    };

    if (!claims.length) {
        return (
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
                <div className="text-3xl mb-2">🧾</div>
                <div className="text-gray-400 text-sm">No purchase claims yet</div>
                <div className="text-gray-600 text-xs mt-1">Submit a claim above to get started</div>
            </div>
        );
    }

    return (
        <div className="space-y-3 bg-gray-900 p-2 rounded-xl text-white border-t-2 border-cyan-200 mt-2">

            {/* Summary row */}
            <div className="flex justify-between items-center text-xs text-gray-400 px-1">
                <span>Total: <strong className="text-white">{claims.length}</strong></span>
                <span>Redeemable: <strong className="text-green-400">{redeemableClaims.length}</strong></span>
                {selectedClaims.length > 0 && (
                    <span className="text-cyan-400 font-bold">
                        Selected: {selectedClaims.length} ({selectedTotal} pts)
                    </span>
                )}
            </div>
            <button
                onClick={refreshClaims}
                disabled={refreshing}
                className="text-[10px] w-fit px-2 py-1 rounded-md bg-gray-800 border border-gray-700 text-gray-300 hover:border-cyan-500/50 transition-colors"
            >
                {refreshing ? "Refreshing..." : "🔄 Refresh"}
            </button>

            {/* Shop filter */}
            <div className="flex gap-2 flex-wrap">
                {["ALL", ...shopOptions.map((s) => s.shopkeeperCode)].map((code) => {
                    const shop = shopOptions.find((s) => s.shopkeeperCode === code);
                    return (
                        <button
                            key={code}
                            onClick={() => setShopFilter(code)}
                            className={`w-fit px-2 py-1 text-[10px] rounded-md border transition-colors ${
                                shopFilter === code
                                    ? "bg-cyan-500 border-cyan-500 text-black font-bold"
                                    : "bg-gray-700 border-gray-600 text-gray-200"
                            }`}
                        >
                            {code === "ALL" ? "All Shops" : shop?.shopName || code}
                        </button>
                    );
                })}
            </div>

            {/* Status filter */}
            <div className="flex gap-2 flex-wrap">
                {["ALL", "Pending", "Approved", "PartialApproved", "Rejected"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`w-24 px-2 py-0.5 text-[10px] rounded-md border transition-colors ${
                            statusFilter === s
                                ? "bg-gray-200 border-gray-200 text-black font-bold"
                                : "bg-gray-700 border-gray-600 text-gray-200"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            {/* Claims - Empty state for filtered results */}
            {filteredClaims.length === 0 ? (
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
                    <div className="text-3xl mb-2">🔍</div>
                    <div className="text-gray-400 text-sm">No claims match this filter</div>
                    <div className="text-gray-600 text-xs mt-1">
                        Try a different status or shop filter
                    </div>
                    {(shopFilter !== "ALL" || statusFilter !== "ALL") && (
                        <button
                            onClick={() => { setShopFilter("ALL"); setStatusFilter("ALL"); }}
                            className="mt-2 text-xs text-cyan-400 underline"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    <AnimatePresence>
                        {filteredClaims.map((claim) => (
                            <ClaimCard
                                key={claim._id}
                                claim={claim}
                                isSelected={isSelected(claim)}
                                onToggle={toggleSelect}
                                canSelect={true}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            )}

            {/* Redemption CTA */}
            <AnimatePresence>
                {selectedClaims.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="sticky bottom-4 z-30 space-y-2"
                    >
                        {message && (
                            <div className={`p-2 rounded-xl text-xs font-bold text-center ${
                                message.type === "success"
                                    ? "bg-green-900/80 text-green-300"
                                    : "bg-red-900/80 text-red-300"
                            }`}>
                                {message.text}
                            </div>
                        )}
                        <button
                            onClick={submitRedemption}
                            disabled={loading || !canCreateVoucher}
                            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-black text-sm shadow-lg shadow-purple-900/40 disabled:opacity-50 active:scale-98 transition-transform"
                        >
                            {loading
                                ? "Submitting..."
                                : canCreateVoucher ? `🎟️ Create Voucher (${selectedTotal} pts)` : `Minimum 100 points required (${selectedTotal}/100)`
                            }
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PurchaseClaimList;
